/* eslint-disable react-hooks/rules-of-hooks */
import React, { createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import { Text, StyleSheet, View, Link } from '@react-pdf/renderer';
import { ReactMarkdownOptions } from 'react-markdown/lib/react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarBreaks from 'remark-breaks';

import colors from '@/design/colors';

interface Props {
  text: string;
}

type ListKind = 'ul' | 'ol';

interface ListContextValue {
  depth: number;
  listType: ListKind;
  indexOffset: number;
}

const ListContext = createContext<ListContextValue>({
  depth: 0,
  listType: 'ul',
  indexOffset: 0,
});

const useListContext = () => useContext(ListContext);

const headings = StyleSheet.create({
  h1: {
    fontWeight: 700,
    fontSize: 14,
    marginVe: 8,
  },
  h2: {
    fontWeight: 600,
    fontSize: 12,
    marginVe: 6,
  },
  h3: {
    fontWeight: 600,
    fontSize: 11,
    marginVe: 4,
  },
  h4: {
    fontWeight: 600,
    fontSize: 10,
    marginVe: 4,
  },
  h5: {
    fontWeight: 600,
    fontSize: 9,
    marginVe: 2,
  },
  h6: {
    fontSize: 9,
    fontWeight: 400,
    textTransform: 'uppercase',
    marginVe: 2,
  },
});

// Lists

const bulletForDepth = (depth: number) => {
  const bullets = ['•', '◦', '▪'];
  return bullets[(depth - 1) % bullets.length] || '•';
};

const listMargin = (depth: number) => ({
  marginLeft: 16,
  marginBottom: depth === 0 ? 8 : 4,
});

// Dedicated renderers to allow safe type checks inside `li`
const UlRenderer = ({ children }: { children?: React.ReactNode }) => {
  const parent = useListContext();
  const depth = parent.depth + 1;
  return (
    <ListContext.Provider value={{ depth, listType: 'ul', indexOffset: 0 }}>
      <View style={[listMargin(depth), { flexDirection: 'column' }]}>
        {children}
      </View>
    </ListContext.Provider>
  );
};

const OlRenderer = ({
  children,
  start,
}: {
  children?: React.ReactNode;
  start?: number;
}) => {
  const parent = useListContext();
  const depth = parent.depth + 1;
  const indexOffset = typeof start === 'number' ? start - 1 : 0;
  return (
    <ListContext.Provider value={{ depth, listType: 'ol', indexOffset }}>
      <View style={listMargin(depth)}>{children}</View>
    </ListContext.Provider>
  );
};

const pdfComponents: ReactMarkdownOptions['components'] = {
  // Headings
  h1: ({ children }) => <Text style={headings.h1}>{children}</Text>,
  h2: ({ children }) => <Text style={headings.h2}>{children}</Text>,
  h3: ({ children }) => <Text style={headings.h3}>{children}</Text>,
  h4: ({ children }) => <Text style={headings.h4}>{children}</Text>,
  h5: ({ children }) => <Text style={headings.h5}>{children}</Text>,
  h6: ({ children }) => <Text style={headings.h6}>{children}</Text>,

  // Paragraph
  p: ({ children }) => <Text>{children}</Text>,
  // Spans (support for pure HTML inserted from editors like CKEditor)
  span: ({ children }) => <Text>{children}</Text>,
  // Links
  a: ({ children, href }) => (
    <Link
      href={href}
      style={{ color: colors.purple100, textDecoration: 'underline' }}
    >
      {children}
    </Link>
  ),
  // Emphasis
  b: ({ children }) => <Text style={{ fontWeight: 'bold' }}>{children}</Text>,
  strong: ({ children }) => (
    <Text style={{ fontWeight: 'bold' }}>{children}</Text>
  ),
  i: ({ children }) => <Text style={{ fontStyle: 'italic' }}>{children}</Text>,
  em: ({ children }) => <Text style={{ fontStyle: 'italic' }}>{children}</Text>,
  del: ({ children }) => (
    <Text style={{ textDecoration: 'line-through' }}>{children}</Text>
  ),
  u: ({ children }) => (
    <Text style={{ textDecoration: 'underline' }}>{children}</Text>
  ),
  ins: ({ children }) => (
    <Text style={{ textDecoration: 'underline' }}>{children}</Text>
  ),
  mark: ({ children }) => (
    <Text
      style={{
        backgroundColor: colors.gray100,
      }}
    >
      {children}
    </Text>
  ),
  small: ({ children }) => (
    <Text style={{ fontSize: 8, color: colors.gray300 }}>{children}</Text>
  ),
  // Horizontal rules
  hr: () => (
    <View
      style={{
        borderBottom: `1px solid ${colors.gray100}`,
        marginVertical: 8,
      }}
    />
  ),
  // Lists
  ul: UlRenderer,
  ol: OlRenderer,
  li: ({ children, index }) => {
    const ctx = useListContext();
    const isOrdered = ctx.listType === 'ol';
    const label = isOrdered
      ? `${ctx.indexOffset + (index ?? 0) + 1}. `
      : `${bulletForDepth(ctx.depth)} `;

    const renderContent = () => {
      const inline: React.ReactNode[] = [];
      const blocks: React.ReactNode[] = [];

      React.Children.forEach(children, child => {
        if (typeof child === 'string' || typeof child === 'number') {
          if (`${child}`.trim() === '') return;
          inline.push(child);
          return;
        }

        if (React.isValidElement(child)) {
          const t = (child as React.ReactElement).type;
          const isSubList = t === UlRenderer || t === OlRenderer;

          if (isSubList) {
            blocks.push(child);
          } else {
            inline.push(child);
          }
          return;
        }

        inline.push(child as React.ReactNode);
      });

      return (
        <View>
          {inline.length > 0 && <Text>{inline}</Text>}
          {blocks.length > 0 && <View>{blocks}</View>}
        </View>
      );
    };

    return (
      <View style={[{ flexDirection: 'row', columnGap: 8 }]}>
        <Text>{label}</Text>
        <View style={{ flex: 1 }}>{renderContent()}</View>
      </View>
    );
  },
};

const DocumentMarkdown: React.FC<Props> = ({ text }) => {
  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw]}
      remarkPlugins={[remarkGfm, remarBreaks]}
      components={pdfComponents}
    >
      {text}
    </ReactMarkdown>
  );
};

export default DocumentMarkdown;
